import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { DEMO_MAX_USERS, addDaysIso, LICENSE_VERTICALS, normalizePrimaryVertical } from './licenseUtils';
import LicenseBranchesEditor from './LicenseBranchesEditor';

const LicenseCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type') === 'demo' ? 'demo' : 'standard';

  const [formData, setFormData] = useState({
    license_type: initialType,
    company_name: '',
    license_key: '',
    tier_id: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    start_date: new Date().toISOString().split('T')[0],
    expiry_date: addDaysIso(new Date().toISOString().split('T')[0], initialType === 'demo' ? 14 : 365),
    max_users: initialType === 'demo' ? DEMO_MAX_USERS : 1,
    status: 'active',
    notes: initialType === 'demo' ? 'Demo / trial license for ChesiWMS evaluation' : '',
    sync_to_wms: initialType === 'demo',
    primary_vertical: 'distribution',
    is_multi_branch: false,
    branches: [],
  });
  const [tiers, setTiers] = useState([]);
  const [error, setError] = useState('');
  const [successInfo, setSuccessInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const isDemo = formData.license_type === 'demo';

  useEffect(() => {
    loadTiers();
  }, []);

  const loadTiers = async () => {
    try {
      const response = await api.getLicenseTiers();
      if (response.success) {
        setTiers(response.data || []);
        if (initialType === 'demo') {
          const demoTier = (response.data || []).find((t) => t.slug === 'demo');
          if (demoTier) {
            setFormData((prev) => ({ ...prev, tier_id: String(demoTier.id) }));
          }
        }
      }
    } catch (err) {
      console.error('Failed to load tiers:', err);
    }
  };

  const handleLicenseTypeChange = (e) => {
    const licenseType = e.target.value;
    const demo = licenseType === 'demo';
    const start = formData.start_date || new Date().toISOString().split('T')[0];
    const demoTier = tiers.find((t) => t.slug === 'demo');

    setFormData({
      ...formData,
      license_type: licenseType,
      max_users: demo ? DEMO_MAX_USERS : formData.max_users,
      expiry_date: demo ? addDaysIso(start, 14) : formData.expiry_date,
      sync_to_wms: demo,
      tier_id: demo && demoTier ? String(demoTier.id) : formData.tier_id,
      notes: demo ? 'Demo / trial license for ChesiWMS evaluation' : '',
    });
  };

  const handleTierChange = (e) => {
    const tierId = e.target.value;
    const selectedTier = tiers.find((t) => t.id === parseInt(tierId, 10));
    setFormData({
      ...formData,
      tier_id: tierId,
      max_users: isDemo ? DEMO_MAX_USERS : selectedTier ? selectedTier.max_users : formData.max_users,
      license_type: selectedTier?.slug === 'demo' ? 'demo' : formData.license_type,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleGenerateKey = async () => {
    try {
      const response = await api.generateLicenseKey();
      if (response.success) {
        setFormData({ ...formData, license_key: response.data.license_key });
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessInfo(null);
    setLoading(true);

    try {
      const payload = {
        ...formData,
        max_users: isDemo ? DEMO_MAX_USERS : Number(formData.max_users),
        primary_vertical: normalizePrimaryVertical(formData.primary_vertical),
        verticals_enabled: [normalizePrimaryVertical(formData.primary_vertical)],
        is_multi_branch: Boolean(formData.is_multi_branch),
        branches: formData.is_multi_branch ? formData.branches : [],
        sync_to_wms: true,
      };
      const response = await api.createLicense(payload);
      if (response.success) {
        if (response.demo_credentials) {
          const syncFailed = response.wms_sync && response.wms_sync.success === false;
          setSuccessInfo({
            ...response.demo_credentials,
            wmsSyncFailed: syncFailed,
            wmsSyncMessage: syncFailed ? (response.wms_sync.message || 'ChesiWMS tenant was not provisioned') : null,
          });
        } else {
          navigate('/licenses');
        }
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {error && (
        <div className="alert alert-danger alert-dismissible">
          <button type="button" className="close" onClick={() => setError('')}>&times;</button>
          {error}
        </div>
      )}

      {successInfo && (
        <div className={`alert ${successInfo.wmsSyncFailed ? 'alert-warning' : 'alert-success'}`}>
          <h5><i className={`fas ${successInfo.wmsSyncFailed ? 'fa-exclamation-triangle' : 'fa-check-circle'} mr-1`} /> Demo license created</h5>
          {successInfo.wmsSyncFailed ? (
            <p className="mb-2">
              The license was saved in ChesiPortal, but ChesiWMS provisioning failed:
              {' '}<strong>{successInfo.wmsSyncMessage}</strong>.
              Set <code>CHESIWMS_API_URL=http://localhost:5001/api</code> in ChesiPortal_API and use Activate on the license to retry.
            </p>
          ) : (
            <p className="mb-1">A dedicated ChesiWMS tenant was provisioned for <strong>{successInfo.company_name}</strong>.</p>
          )}
          <ul className="mb-2">
            <li><strong>Login email:</strong> {successInfo.email}</li>
            <li><strong>Temporary password:</strong> <code>{successInfo.password}</code></li>
            <li><strong>User limit:</strong> {successInfo.max_users || DEMO_MAX_USERS} (create additional users inside ChesiWMS)</li>
          </ul>
          <p className="mb-2 text-muted small">Share these credentials with the client. Each demo license uses its own isolated company/tenant.</p>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate('/licenses')}>
            Back to licenses
          </button>
        </div>
      )}

      {!successInfo && (
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">{isDemo ? 'Create Demo License' : 'Create New License'}</h3>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="card-body">
                  <div className="form-group">
                    <label>License Type *</label>
                    <select
                      className="form-control"
                      name="license_type"
                      value={formData.license_type}
                      onChange={handleLicenseTypeChange}
                    >
                      <option value="standard">Standard</option>
                      <option value="demo">Demo (ChesiWMS trial)</option>
                    </select>
                    {isDemo && (
                      <small className="form-text text-muted">
                        Demo licenses provision a separate ChesiWMS tenant (max {DEMO_MAX_USERS} users), isolated from other clients.
                      </small>
                    )}
                  </div>

                  {isDemo && (
                    <div className="alert alert-info py-2">
                      <i className="fas fa-info-circle mr-1" />
                      Use a <strong>unique company name</strong> per prospect so each demo keeps its own data.
                      Login is blocked after the active period ends until you click <strong>Activate</strong> in the portal.
                    </div>
                  )}

                  <div className="form-group">
                    <label>License Tier</label>
                    <select
                      className="form-control"
                      name="tier_id"
                      value={formData.tier_id}
                      onChange={handleTierChange}
                    >
                      <option value="">Select a tier (optional)</option>
                      {tiers.map((tier) => (
                        <option key={tier.id} value={tier.id}>
                          {tier.name} - {tier.currency} {tier.price_monthly}/month
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Company Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      placeholder={isDemo ? 'e.g. Acme Foods Demo' : ''}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>License Key</label>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        name="license_key"
                        value={formData.license_key}
                        onChange={handleChange}
                        placeholder="Leave empty to auto-generate"
                      />
                      <div className="input-group-append">
                        <button type="button" className="btn btn-default" onClick={handleGenerateKey}>
                          <i className="fas fa-key"></i> Generate
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Contact Name</label>
                        <input
                          type="text"
                          className="form-control"
                          name="contact_name"
                          value={formData.contact_name}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Contact Email {isDemo ? '*' : ''}</label>
                        <input
                          type="email"
                          className="form-control"
                          name="contact_email"
                          value={formData.contact_email}
                          onChange={handleChange}
                          required={isDemo}
                        />
                        {isDemo && (
                          <small className="form-text text-muted">Used as the ChesiWMS admin login for this demo tenant.</small>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Contact Phone</label>
                    <input
                      type="text"
                      className="form-control"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="card card-outline card-secondary mb-3">
                    <div className="card-header py-2">
                      <strong>Active period</strong>
                    </div>
                    <div className="card-body py-3">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group mb-md-0">
                            <label>Start Date *</label>
                            <input
                              type="date"
                              className="form-control"
                              name="start_date"
                              value={formData.start_date}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-0">
                            <label>End Date *</label>
                            <input
                              type="date"
                              className="form-control"
                              name="expiry_date"
                              value={formData.expiry_date}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </div>
                      </div>
                      <small className="form-text text-muted mt-2">
                        ChesiWMS login works only between these dates (inclusive). After the end date, use Activate in the license list.
                      </small>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Max Users *</label>
                        <input
                          type="number"
                          className="form-control"
                          name="max_users"
                          value={formData.max_users}
                          onChange={handleChange}
                          min="1"
                          max={isDemo ? DEMO_MAX_USERS : undefined}
                          readOnly={isDemo}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          className="form-control"
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                          disabled={isDemo}
                        >
                          <option value="active">Active</option>
                          <option value="expiring">Expiring</option>
                          <option value="expired">Expired</option>
                          <option value="suspended">Suspended</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Business Vertical *</label>
                    <select
                      className="form-control"
                      name="primary_vertical"
                      value={formData.primary_vertical}
                      onChange={handleChange}
                      required
                    >
                      {LICENSE_VERTICALS.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                    <small className="form-text text-muted">
                      This client will only see modules for the selected vertical in ChesiWMS.
                    </small>
                  </div>

                  <LicenseBranchesEditor
                    maxUsers={formData.max_users}
                    isMultiBranch={formData.is_multi_branch}
                    branches={formData.branches}
                    onMultiBranchChange={(checked) =>
                      setFormData((prev) => ({ ...prev, is_multi_branch: checked }))
                    }
                    onBranchesChange={(branches) => setFormData((prev) => ({ ...prev, branches }))}
                  />

                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      className="form-control"
                      name="notes"
                      rows="3"
                      value={formData.notes}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="card-footer">
                  <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                    <i className="fas fa-save"></i> {loading ? 'Creating...' : isDemo ? 'Create Demo & Provision WMS' : 'Create License'}
                  </button>
                  <button type="button" className="btn btn-default btn-sm ml-2" onClick={() => navigate('/licenses')}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LicenseCreate;
